import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Tooltip as ReactTooltip } from "react-tooltip"
import classNames from "classnames"
import styled from "styled-components"

import { useLazyGetContractorUniversityUrlQuery } from "store/api/contractorsApi"
import { useLazyGetNewOrdersQuantityQuery } from "store/api/ordersApi"
import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import { ContractorUserRole } from "types/ContractorUserEnum"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconKnowledgeCenter } from "assets/svg/knowledge-center-icon.svg"
import logo from "assets/svg/logo-white.svg"
import { ReactComponent as IconAlerts } from "assets/svg/menu-alerts.svg"
import { ReactComponent as IconCustomers } from "assets/svg/menu-customers.svg"
import { ReactComponent as IconDashboard } from "assets/svg/menu-dashboard.svg"
import { ReactComponent as IconMessages } from "assets/svg/menu-messages.svg"
import { ReactComponent as IconMonitoring } from "assets/svg/menu-monitoring.svg"
import { ReactComponent as IconOrders } from "assets/svg/menu-orders.svg"
import { ReactComponent as IconPerformance } from "assets/svg/menu-performance.svg"

import { isMobileDevice } from "utils/deviceDetection"
import { Hamburger } from "components/common/Hamburger"
import MenuWrapper from "components/common/MenuWrapper"
import { NavLink } from "components/common/NavLink"
import { UserMenu } from "components/common/UserMenu"

import { RightSideIconsWrapper } from "../styled"

import localStyles from "./index.module.scss"

const StyledHeader = styled.header<{ backgroundStyle: string; isSticky: boolean }>`
  ${({ isSticky, backgroundStyle }) => isSticky && `background: ${backgroundStyle} !important;`}
`

const KnowledgeIconWrapper = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  margin-left: 10px;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
    cursor: pointer;
  }
`

const LogoWrapper = styled(Link)`
  margin-top: 2px;
  align-self: baseline;
  min-width: 100px;
`

const StyledLogo = styled.img`
  top: 2px;
  position: absolute;
  min-height: 64px;
  max-height: 80px;
  object-fit: contain;
`

export const ContractorTopbar = () => {
  const [triggerGetNewOrdersQuantity, { data }] = useLazyGetNewOrdersQuantityQuery()
  const [triggerGetContractorUniversityUrl] = useLazyGetContractorUniversityUrlQuery()
  const [isSticky, setSticky] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const userProfile = useAppSelector(state => state.user.userProfile)
  const current_subscription = userProfile?.contractor?.subscription_plan
  const hasEPlusPlan = current_subscription === SubscriptionPlan.E_PLUS
  const currentTheme = useAppSelector(contractorThemeSelector)
  const userLogo = useAppSelector(userLogoSelector)
  const [universityUrl, setUniversityUrl] = useState<string>("")
  const mobileDevice = isMobileDevice()
  const isTechnician = userProfile?.type === ContractorUserRole.TECHNICIAN

  const closeMenu = () => {
    setOpen(false)
  }

  const handleScroll = () => {
    const distance = window.scrollY

    if (distance > 0 && !isSticky) {
      setSticky(true)
    } else if (distance === 0 && isSticky) {
      setSticky(false)
    }
  }

  useEffect(() => {
    const setStickyOnScroll = () => handleScroll()

    window.addEventListener("scroll", setStickyOnScroll)

    return () => {
      window.removeEventListener("scroll", setStickyOnScroll)
    }
  }, [isSticky])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetNewOrdersQuantity()
      } catch (error) {
        console.error(error)
      }
    }

    const intervalId = setInterval(fetchData, 60000)
    fetchData()
    return () => clearInterval(intervalId)
  }, [])

  const getBackgroundStyle = (hasEPlusPlan: boolean) => {
    if (hasEPlusPlan) {
      return currentTheme.backgroundGradient
    }
    return ContractorThemes.default.backgroundGradient
  }

  const fetchUniversityUrl = async () => {
    const res = await triggerGetContractorUniversityUrl()
    if (res.data?.data) {
      setUniversityUrl(res.data?.data)
      return res.data?.data
    }
  }

  const handleOpenContractorUniversityUrl = async () => {
    const url = await fetchUniversityUrl()
    if (url) {
      window.open(url, "_blank", "noreferrer")
    }
  }

  useEffect(() => {
    if (mobileDevice && current_subscription === SubscriptionPlan.E_PLUS) {
      fetchUniversityUrl()
      setInterval(() => {
        fetchUniversityUrl()
      }, 86400)
    }
  }, [])

  return (
    <StyledHeader
      backgroundStyle={getBackgroundStyle(hasEPlusPlan)}
      isSticky={isSticky}
      className={classNames(styles.topbar, { [styles.sticky]: isSticky })}
    >
      <LogoWrapper className={styles.logoWrapper} to="/">
        <StyledLogo className={styles.logo} src={userLogo || logo} alt="logo" />
      </LogoWrapper>
      <MenuWrapper backgroundStyle={getBackgroundStyle(hasEPlusPlan)} isOpen={isOpen} onClose={closeMenu}>
        {!isTechnician && (
          <NavLink to="/dashboard" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
            <IconDashboard className={styles.menuIcon} />
            Dashboard
          </NavLink>
        )}
        <NavLink to="/customers" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
          <IconCustomers className={styles.menuIcon} />
          Customers
        </NavLink>
        {!isTechnician && (
          <NavLink to="/orders" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
            <IconOrders className={styles.menuIcon} />
            Orders
            {Boolean(data?.data?.quantity) && <span className={styles.redDot}>{data?.data?.quantity}</span>}
          </NavLink>
        )}
        {!isTechnician && (
          <NavLink to="/messages" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
            <IconMessages className={styles.menuIcon} />
            Messages
          </NavLink>
        )}
        <NavLink to="/alerts" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
          <IconAlerts className={styles.menuIcon} />
          Alerts
        </NavLink>
        {!isTechnician && (
          <NavLink to="/perf-test" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
            <IconPerformance className={classNames(styles.menuIcon, styles.menuIconPerformance)} />
            Performance Test
          </NavLink>
        )}
        <NavLink to="/monitoring" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
          <IconMonitoring className={styles.menuIcon} />
          Monitoring
        </NavLink>
      </MenuWrapper>
      <RightSideIconsWrapper>
        <Hamburger onClick={() => setOpen(!isOpen)} isOpen={isOpen} />
        {current_subscription === SubscriptionPlan.E_PLUS && (
          <KnowledgeIconWrapper>
            {mobileDevice ? (
              <a href={universityUrl} data-tooltip-id={"tooltip-knowledge-center"} target="_blank" rel="noreferrer">
                <IconKnowledgeCenter />
              </a>
            ) : (
              <span data-tooltip-id={"tooltip-knowledge-center"} onClick={handleOpenContractorUniversityUrl}>
                <IconKnowledgeCenter />
              </span>
            )}
            <ReactTooltip delayShow={500} className={localStyles.tooltip} id={"tooltip-knowledge-center"}>
              Contractor University
            </ReactTooltip>
          </KnowledgeIconWrapper>
        )}
        <UserMenu />
      </RightSideIconsWrapper>
    </StyledHeader>
  )
}
